import { Injectable, computed, effect, signal } from '@angular/core';
import { isAdminUser } from '@shared/general';
import { getActiveTimeZone, isSameTimeZone, isValidTimeZone, setActiveTimeZone, setSiteTimeZone } from '@shared/time';
import dayjs from 'dayjs';
import { LocalStorageService } from './local-storage.service';
import { UserService } from './user.service';

export type TimezoneMode = 'site' | 'utc' | 'local';

export interface TimezoneOption {
  value: TimezoneMode;
  label: string;
}

const SITE_TZ_STORAGE_KEY = 'siteTimeZone';
const MODE_STORAGE_KEY = 'timezoneMode';
const SELECTION_ENABLED_STORAGE_KEY = 'timezoneSelectionEnabled';
const MODES: readonly TimezoneMode[] = ['site', 'utc', 'local'];

const USER_TIMEZONE_SWITCHING_ENABLED = false;

@Injectable({
  providedIn: 'root',
})
export class TimezoneService {
  readonly siteTimeZone = signal<string>(this.readCachedSiteTimeZone() ?? getActiveTimeZone());

  readonly mode = signal<TimezoneMode>(this.readCachedMode());

  readonly selectionEnabled = signal<boolean>(this.readCachedSelectionEnabled());

  readonly localTimeZone = dayjs.tz.guess();

  readonly effectiveMode = computed<TimezoneMode>(() => {
    if (!this.isAdmin()) {
      return USER_TIMEZONE_SWITCHING_ENABLED ? this.mode() : 'site';
    }
    return this.selectionEnabled() ? this.mode() : 'site';
  });

  readonly activeTimeZone = computed(() => {
    switch (this.effectiveMode()) {
      case 'utc':
        return this.isAdmin() ? 'UTC' : this.siteTimeZone();
      case 'local':
        return this.localTimeZone;
      case 'site':
      default:
        return this.siteTimeZone();
    }
  });

  readonly isAdmin = computed(() => {
    const user = this.storage.userInfo();
    return !!user && isAdminUser(user);
  });

  readonly timezoneOptions = computed<TimezoneOption[]>(() => {
    const site = this.siteTimeZone();
    const options: TimezoneOption[] = [{ value: 'site', label: site }];

    if (!this.isAdmin() && !USER_TIMEZONE_SWITCHING_ENABLED) {
      return options;
    }

    if (!isSameTimeZone(this.localTimeZone, site)) {
      options.push({ value: 'local', label: 'Local' });
    }

    if (this.isAdmin() && !isSameTimeZone(site, 'UTC')) {
      options.push({ value: 'utc', label: 'UTC' });
    }

    return options;
  });

  constructor(
    private userService: UserService,
    private storage: LocalStorageService,
  ) {
    effect(() => setActiveTimeZone(this.activeTimeZone()));
    effect(() => setSiteTimeZone(this.siteTimeZone()));
  }

  init(): void {
    this.userService.getPreference().subscribe({
      next: (res) => {
        const candidate = res?.data?.timeZone;
        if (isValidTimeZone(candidate)) {
          this.siteTimeZone.set(candidate);
          localStorage.setItem(SITE_TZ_STORAGE_KEY, candidate);
        }
      },
      error: () => {},
    });
  }

  setMode(mode: TimezoneMode): void {
    const changed = mode !== this.mode();
    this.mode.set(mode);
    localStorage.setItem(MODE_STORAGE_KEY, mode);

    if (changed) {
      window.location.reload();
    }
  }

  setSelectionEnabled(enabled: boolean): void {
    this.selectionEnabled.set(enabled);
    localStorage.setItem(SELECTION_ENABLED_STORAGE_KEY, enabled ? '1' : '0');

    if (!enabled && this.mode() !== 'site') {
      this.setMode('site');
    }
  }

  private readCachedSiteTimeZone(): string | null {
    const cached = localStorage.getItem(SITE_TZ_STORAGE_KEY);
    return isValidTimeZone(cached) ? cached : null;
  }

  private readCachedMode(): TimezoneMode {
    const cached = localStorage.getItem(MODE_STORAGE_KEY);
    return (MODES as string[]).includes(cached ?? '') ? (cached as TimezoneMode) : 'site';
  }

  private readCachedSelectionEnabled(): boolean {
    return localStorage.getItem(SELECTION_ENABLED_STORAGE_KEY) === '1';
  }
}
